/**
 * ============================================================================
 * COMPOSABLE: SAVE STATUS
 * ============================================================================
 * Отслеживание статуса сохранения с retry механизмом
 * ============================================================================
 */

import { ref, computed } from 'vue'

const SAVE_STATUS = {
	IDLE: 'idle',
	SAVING: 'saving',
	SAVED: 'saved',
	ERROR: 'error',
	RETRYING: 'retrying',
}

const MAX_RETRIES = 3
const RETRY_DELAY = 2000

export function useSaveStatus() {
	const status = ref(SAVE_STATUS.IDLE)
	const error = ref(null)
	const retryCount = ref(0)
	const lastSavedAt = ref(null)

	const isSaving = computed(() => 
		status.value === SAVE_STATUS.SAVING || status.value === SAVE_STATUS.RETRYING
	)

	const hasError = computed(() => status.value === SAVE_STATUS.ERROR)

	const canRetry = computed(() => retryCount.value < MAX_RETRIES)

	// Выполнить сохранение с retry
	const executeSave = async (saveFn, options = {}) => {
		const { 
			onSuccess = null, 
			onError = null,
			maxRetries = MAX_RETRIES,
			retryDelay = RETRY_DELAY,
		} = options

		const attemptSave = async (attempt = 0) => {
			try {
				status.value = attempt > 0 ? SAVE_STATUS.RETRYING : SAVE_STATUS.SAVING
				retryCount.value = attempt

				const result = await saveFn()

				status.value = SAVE_STATUS.SAVED
				error.value = null
				retryCount.value = 0
				lastSavedAt.value = new Date()

				if (onSuccess) {
					onSuccess(result)
				}

				// Сбросить статус через 3 секунды
				setTimeout(() => {
					if (status.value === SAVE_STATUS.SAVED) {
						status.value = SAVE_STATUS.IDLE
					}
				}, 3000)

				return result
			} catch (err) {
				console.error(`❌ Ошибка сохранения (попытка ${attempt + 1}/${maxRetries}):`, err)

				if (attempt < maxRetries - 1) {
					// Retry
					await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)))
					return attemptSave(attempt + 1)
				} else {
					// Все попытки исчерпаны
					status.value = SAVE_STATUS.ERROR
					error.value = err
					retryCount.value = attempt + 1

					if (onError) {
						onError(err)
					}

					throw err
				}
			}
		}

		return attemptSave()
	}

	// Сбросить статус
	const reset = () => {
		status.value = SAVE_STATUS.IDLE
		error.value = null
		retryCount.value = 0
	}

	// Получить текст статуса
	const getStatusText = () => {
		switch (status.value) {
			case SAVE_STATUS.SAVING:
				return 'Сохранение...'
			case SAVE_STATUS.SAVED:
				return 'Сохранено'
			case SAVE_STATUS.ERROR:
				return 'Ошибка сохранения'
			case SAVE_STATUS.RETRYING:
				return `Повтор (${retryCount.value}/${MAX_RETRIES})...`
			default:
				return ''
		}
	}

	// Получить иконку статуса
	const getStatusIcon = () => {
		switch (status.value) {
			case SAVE_STATUS.SAVING:
			case SAVE_STATUS.RETRYING:
				return 'mdi-loading mdi-spin'
			case SAVE_STATUS.SAVED:
				return 'mdi-check-circle'
			case SAVE_STATUS.ERROR:
				return 'mdi-alert-circle'
			default:
				return ''
		}
	}

	// Получить цвет статуса
	const getStatusColor = () => {
		switch (status.value) {
			case SAVE_STATUS.SAVING:
			case SAVE_STATUS.RETRYING:
				return 'info'
			case SAVE_STATUS.SAVED:
				return 'success'
			case SAVE_STATUS.ERROR:
				return 'error'
			default:
				return ''
		}
	}

	return {
		status,
		error,
		retryCount,
		lastSavedAt,
		isSaving,
		hasError,
		canRetry,
		executeSave,
		reset,
		getStatusText,
		getStatusIcon,
		getStatusColor,
		SAVE_STATUS,
	}
}
